import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Input, Modal, Select } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import {
  ROLES,
  ROLE_LABEL,
  updateUserSchema,
  type UpdateUserInput,
  type User,
} from '../schemas';
import { useAreas, useUpdateUser } from '../api';

interface Props {
  user: User | null;
  onClose: () => void;
}

export function EditUserModal({ user, onClose }: Props) {
  const update = useUpdateUser();
  const { data: areas = [] } = useAreas();
  const [areaIds, setAreaIds] = useState<number[]>([]);
  const [areasDirty, setAreasDirty] = useState(false);
  const form = useForm<UpdateUserInput>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      name: user?.name ?? '',
      cargo: user?.cargo ?? '',
      role: (user?.role as UpdateUserInput['role']) ?? 'colaborador',
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name,
        cargo: user.cargo ?? '',
        role: user.role as UpdateUserInput['role'],
      });
      setAreaIds((user.areas ?? []).map((a) => Number(a.id)));
      setAreasDirty(false);
    }
  }, [user, form]);

  function toggleArea(id: number) {
    setAreaIds((prev) => {
      const has = prev.includes(id);
      const next = has ? prev.filter((x) => x !== id) : [...prev, id];
      return next;
    });
    setAreasDirty(true);
  }

  return (
    <Modal
      open={user !== null}
      onOpenChange={(v) => !v && onClose()}
      title={user ? `Editar — ${user.name}` : ''}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              void form.handleSubmit((values) => {
                if (!user) return;
                const patch: UpdateUserInput = { ...values };
                if (areasDirty) patch.area_ids = areaIds;
                update.mutate(
                  { id: user.id, patch },
                  { onSuccess: () => onClose() },
                );
              })()
            }
            disabled={update.isPending || (!form.formState.isDirty && !areasDirty)}
          >
            {update.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      {user && (
        <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
          <Stack gap="md">
            <Field
              label="Nome"
              htmlFor="edit-usr-name"
              error={form.formState.errors.name?.message}
            >
              <Input
                id="edit-usr-name"
                invalid={!!form.formState.errors.name}
                {...form.register('name')}
              />
            </Field>
            <Field
              label="Cargo"
              htmlFor="edit-usr-cargo"
              error={form.formState.errors.cargo?.message}
            >
              <Input id="edit-usr-cargo" {...form.register('cargo')} />
            </Field>
            <Field
              label="Papel"
              htmlFor="edit-usr-role"
              error={form.formState.errors.role?.message}
            >
              <Select id="edit-usr-role" {...form.register('role')}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Áreas de atuação" htmlFor="edit-usr-areas">
              <div className="flex flex-col gap-2">
                <Select
                  id="edit-usr-areas"
                  value=""
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    if (!id || areaIds.includes(id)) return;
                    setAreaIds((prev) => [...prev, id]);
                    setAreasDirty(true);
                    e.target.value = '';
                  }}
                >
                  <option value="">+ Adicionar área…</option>
                  {areas
                    .filter((a) => !areaIds.includes(Number(a.id)))
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nome}
                      </option>
                    ))}
                </Select>
                {areaIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {areaIds.map((id) => {
                      const a = areas.find((x) => Number(x.id) === id);
                      if (!a) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 text-xs font-medium pl-2.5 pr-1 py-0.5 rounded-full"
                          style={{
                            background: a.cor ? `${a.cor}1f` : 'var(--surface-2)',
                            color: a.cor ?? 'var(--text-2)',
                          }}
                        >
                          {a.nome}
                          <button
                            type="button"
                            onClick={() => toggleArea(id)}
                            aria-label={`Remover ${a.nome}`}
                            className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-black/10"
                            style={{ color: a.cor ?? 'var(--text-muted)' }}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                {areas.length === 0 && (
                  <span className="text-xs text-text-muted">
                    Nenhuma área cadastrada.
                  </span>
                )}
              </div>
            </Field>
          </Stack>
          {update.error && (
            <p className="text-xs text-danger">
              {update.error instanceof ApiError ? update.error.message : 'Falha ao atualizar'}
            </p>
          )}
        </form>
      )}
    </Modal>
  );
}
